import { deliveryFeeCents, formatCents, pickZone, tt, type DeliveryZone } from '@agos/shared'

/**
 * The delivery fee on a staff-taken order — the last unwired piece of `/new-order`.
 *
 * `findZoneForPoint` and `deliveryFeeCents` were ported with the page and tested, and **nothing called
 * them**, so `deliveryFee: 0` went into every order this screen has ever made. Measured against the live
 * zones that is not a rounding difference: 31 zones, every one with tiers, charging **3,00 € to 5,00 €**.
 * A delivery order taken over the phone was written with no delivery charge at all.
 *
 * **Why a zone picker and not an address lookup.** Legacy resolves the zone from a geocoded point, and
 * `GET /geocode/search` used to answer `{"error": "Google Maps API key not configured"}` here — so the
 * autocomplete could not be built or verified, and building it blind is the one thing this port does
 * not do. The zones are named after settlements (*Bánov štrkáreň*, *Černík*, *Šurany (4)*, *Hul*), which
 * is exactly the question the person answering the phone asks anyway. So the zone is chosen, the fee
 * follows from it and from the cart total, and the address stays free text.
 *
 * **The key arrived on 2026-08-03 and this said otherwise until 2026-08-05.** `?q=Nitrianska 16 Surany`
 * returns live Places suggestions. Note what does and does not change: the second reason — the zones are
 * named after the villages the caller is already naming — never depended on the key, so the picker stays
 * the right default. What changes is that it is now a choice rather than the only thing possible, and
 * `findZoneForPoint` can become the fallback it was always ported and tested to be.
 *
 * **A minimum is not a fee of zero.** `deliveryFeeCents` answers `null` below `zone.minOrder`, which is
 * the honest answer — Černík will not deliver under 20,00 €. Rendering that as `0,00 €` would put a free
 * delivery on an order the restaurant refuses to make.
 */

export interface ZoneChoice {
  id: string
  name: string
  zone: DeliveryZone
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

/** The centre as the endpoint sends it, or undefined - `{lat, lng}` with numbers or nothing. */
function point(value: unknown): { lat: number; lng: number } | undefined {
  const row = value as Record<string, unknown> | null | undefined
  if (!row) return undefined
  const lat = row.lat ?? row.latitude ?? row.center_lat
  const lng = row.lng ?? row.lon ?? row.longitude ?? row.center_lng
  return typeof lat === 'number' && typeof lng === 'number' ? { lat, lng } : undefined
}

/** Every switched-on zone, by name. A zone somebody turned off is not a delivery this shift. */
export function zoneChoicesOf(payload: unknown): ZoneChoice[] {
  if (!Array.isArray(payload)) return []
  return payload
    .flatMap((entry) => {
      const row = entry as Record<string, unknown>
      const id = text(row.id)
      if (id === null || row.enabled === false) return []
      return [
        {
          id,
          name: text(row.name) ?? id,
          zone: {
            // **The name and the geometry are carried, not dropped** - §9.346. Only `minOrder` and
            // `tiers` were kept here, because the fee was all this screen asked of a zone; `pickZone`
            // settles a tie of equally-named zones by the address and then by the depot, and it can do
            // neither from a zone with no name and no centre. Same shape as FB-505's finding: read off
            // the wire and thrown away one layer in.
            id: id,
            name: text(row.name) ?? id,
            center: point(row.center ?? row.centre),
            radius: typeof row.radius === 'number' ? row.radius : undefined,
            minOrder: (row.minOrder ?? null) as DeliveryZone['minOrder'],
            tiers: Array.isArray(row.tiers) ? (row.tiers as DeliveryZone['tiers']) : [],
          } as DeliveryZone,
        },
      ]
    })
    .sort((left, right) => left.name.localeCompare(right.name, 'sk'))
}

export function chosenZone(zoneId: string | null, choices: readonly ZoneChoice[]): ZoneChoice | null {
  if (zoneId === null || zoneId === '') return null
  return choices.find((choice) => choice.id === zoneId) ?? null
}

/**
 * What this order costs to deliver, or `null` because there is no answer yet.
 *
 * `null` covers two different things and the caller has to tell them apart, which is what
 * {@link feeNote} is for: no zone chosen, and a zone whose minimum this order has not reached.
 */
export function feeCentsFor(chosen: ZoneChoice | null, subtotalCents: number): number | null {
  if (chosen === null) return null
  return deliveryFeeCents(subtotalCents / 100, chosen.zone)
}

/** Whether the order is below the chosen zone's minimum — the reason a fee can be absent. */
export function belowMinimum(chosen: ZoneChoice | null, subtotalCents: number): boolean {
  return chosen !== null && feeCentsFor(chosen, subtotalCents) === null
}

export function minimumCentsOf(chosen: ZoneChoice | null): number {
  if (chosen === null) return 0
  const raw = chosen.zone.minOrder
  if (typeof raw === 'number') return Math.round(raw * 100)
  if (typeof raw === 'string' && raw !== '') return Math.round(Number(raw.replace(',', '.')) * 100) || 0
  return 0
}

/**
 * The line under the picker.
 *
 * Three states, and none of them is a bare number: no zone yet, below the minimum with the shortfall
 * named, and a fee with the total it was computed from — because the same zone charges differently at
 * 19,99 € and at 20,00 € and somebody adding one more pizza should see the fee move.
 */
export function feeNote(
  chosen: ZoneChoice | null,
  subtotalCents: number,
  address = '',
  zoneCount = -1,
): string {
  // §9.161 — this said *"Vyberte zónu"* and pointed at a control that no longer exists. Legacy's two
  // lines instead: nothing typed yet is not a problem, and a typed address that matches no zone is
  // one the dispatcher has to price by hand.
  if (chosen === null) {
    if (address.trim() === '') return tt('Zadajte adresu - cenu dovozu určíme automaticky.')
    // §9.354, FB-631 - **a branch with no zones is a different fact from an address that matches
    // none of them**, and this line used to state the second while meaning the first. Measured on
    // the owner's own data: Šurany has 31 zones, Nesvady and Sereď have **none**, so on those two
    // no address can ever match and *"Zóna nerozpoznaná"* sends the dispatcher to re-check a
    // spelling that was never the problem. The action is the same - type the price - but a reason
    // somebody can act on is the difference between a workaround and a dead end.
    //
    // `-1` is *not told*, which is what keeps this additive: a caller that passes nothing gets the
    // wording it got before, unchanged.
    if (zoneCount === 0) {
      return tt('Táto pobočka nemá nastavené zóny dovozu - zadajte cenu dovozu ručne nižšie.')
    }
    return tt('Zóna nerozpoznaná - zadajte cenu dovozu ručne nižšie.')
  }
  const cents = feeCentsFor(chosen, subtotalCents)
  if (cents === null) {
    const short = minimumCentsOf(chosen) - subtotalCents
    return `${chosen.name}: ${tt('minimálna objednávka je')} ${formatCents(minimumCentsOf(chosen))}, ${tt('chýba')} ${formatCents(Math.max(0, short))}.`
  }
  return `${chosen.name}: ${tt('doprava')} ${formatCents(cents)} ${tt('pri sume')} ${formatCents(subtotalCents)}.`
}

/**
 * What stops the order being placed.
 *
 * Only for a delivery. A zone below its minimum is refused here rather than at the door: the restaurant
 * has said it will not drive for less, and taking the order anyway commits somebody to the drive.
 */
export function deliveryProblems(
  isDelivery: boolean,
  chosen: ZoneChoice | null,
  subtotalCents: number,
): string[] {
  if (!isDelivery) return []
  if (chosen === null) return [tt('Vyberte zónu doručenia')]
  if (belowMinimum(chosen, subtotalCents)) {
    return [
      `${tt('Objednávka nedosahuje minimum zóny')} ${chosen.name} (${formatCents(minimumCentsOf(chosen))})`,
    ]
  }
  return []
}

/**
 * Strips a zone name or an address down to what can honestly be compared — §9.119.
 *
 * Diacritics go because nobody types *Šurany* into a phone order at speed; case goes for the same
 * reason; a bracketed suffix goes because *Šurany (4)* is one settlement numbered by the restaurant,
 * and the caller says "Šurany".
 */
function comparable(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\([^)]*\)/g, ' ')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

export interface ZoneGuess {
  /** The zone to select, or null when the address names none — or names more than one. */
  zone: ZoneChoice | null
  /** Every zone the address could mean. More than one is why `zone` may be null. */
  matches: ZoneChoice[]
}

/**
 * The zone an address names — §9.119, the owner's point 5.
 *
 * Legacy's rule, and the reason the picker should rarely need touching: the zones are named after
 * settlements, so the address the caller dictates already contains the answer. Legacy does a plain
 * `includes` on the lowercased address; this normalises both sides so an unaccented *Surany* still
 * finds *Šurany (4)*.
 *
 * **Ambiguity is reported, not guessed.** *Šurany (2)* and *Šurany (4)* are different fee tables and
 * an address naming only "Surany" does not choose between them. Picking one would put a wrong
 * delivery charge on the bill silently, which is the failure this whole area exists to stop.
 */
export function zoneForAddress(
  address: string,
  choices: readonly ZoneChoice[],
  depot?: { lat: number; lng: number } | null,
): ZoneGuess {
  const haystack = comparable(address)
  if (haystack.length < 3) return { zone: null, matches: [] }

  const matches = choices.filter((choice) => {
    const needle = comparable(choice.name)
    return needle.length >= 3 && haystack.includes(needle)
  })
  // The longest name wins outright where one contains another — "Bánov štrkáreň" over "Bánov" — because
  // the more specific name is the one the caller bothered to say.
  const longest = matches.reduce((best, candidate) => {
    const bestLength = comparable(best?.name ?? '').length
    return comparable(candidate.name).length > bestLength ? candidate : best
  }, matches[0])
  const contested = matches.filter(
    (candidate) => comparable(candidate.name).length === comparable(longest?.name ?? '').length,
  )
  if (contested.length <= 1) return { zone: longest ?? null, matches }
  return { zone: resolveTie(contested, address, depot ?? null), matches }
}

/**
 * Which of several equally-named zones the caller meant - §9.346, the owner's C1.
 *
 * **A tie used to mean no zone**, and no zone means no fee: *Záhradná 10, Šurany 942 01* folds to the
 * same string as `Šurany`, `Šurany (2)`, `Šurany (3)` and `Šurany (4)`, whose fees run from 0,50 to
 * 4,00. The note that reported it asked the dispatcher to *vyberte jednu* from the picker **§9.161
 * deliberately deleted**, so the tie had to be resolved rather than announced.
 *
 * **`pickZone` settles it, and no new rule is needed** - §10A.121, the same four steps in Java and
 * in the customer checkout. Its step 3 is why: `fold` keeps a bracketed suffix where `comparable`
 * strips one, so of `Šurany`, `Šurany (2)`, `(3)` and `(4)` only the bare name is *found in the
 * address the caller dictated*, and the rule picks it outright. That is 0,50, which is what a
 * dispatcher picked from the list while it existed.
 *
 * **A first version of this had a special case for the bare name and it was redundant** - removed
 * when a mutation deleting it left every test green, which is the useful kind of surviving mutant.
 * One rule, three readers, and this is not a fourth.
 *
 * Step 4 - nearest the depot, then smallest radius - decides a tie of numbered zones with no bare
 * name among them, and it is applied **only** to the tie. Run over text matches generally it would
 * hand a town address a 474 m sub-area at four times the fee.
 */
function resolveTie(
  contested: readonly ZoneChoice[],
  address: string,
  depot: { lat: number; lng: number } | null,
): ZoneChoice | null {
  const picked = pickZone(
    contested.map((candidate) => candidate.zone),
    address,
    depot,
  )
  // Nothing measurable - no name found in the address, no centre, no radius - leaves list order,
  // which `zoneChoicesOf` has already sorted. Deterministic, and never null: a tie that answers
  // *no zone* is the reported bug.
  if (picked === null) return contested[0] ?? null
  return contested.find((candidate) => candidate.zone === picked) ?? contested[0] ?? null
}
